import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest, type Envelope } from "../api/client";
import { readClaims } from "../auth/token";
import type { AdvancedFilter, SavedFilter } from "./filter-logic";

// THE SAVED-FILTER ENDPOINTS OF `advanced-filters.service.ts`, VERBATIM

const user = () => String(readClaims()?.userId ?? "anonymous");
const savedKey = () => [user(), "active-session-filters"];

// `GetActiveSessionFilters`: THE SIGNED-IN USER'S SAVED FILTERS (THE SERVER SCOPES BY TOKEN)
export function useSavedFilters() {
  return useQuery({
    queryKey: savedKey(),
    queryFn: ({ signal }) =>
      apiRequest<Envelope<SavedFilter[] | null>>("/ht/get-active-session-filters", { signal }).then((r) => r.data ?? []),
    // `my-filters` RE-READS THE LIST EACH TIME THE TAB OPENS
    staleTime: 0,
  });
}

// `CreateActiveSessionFilter`: THE FILTER IS STORED AS A JSON STRING
export function useCreateSavedFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { name: string; filter: AdvancedFilter }) =>
      apiRequest<unknown>("/ht/create-active-session-filter", {
        method: "POST",
        body: { name: v.name, user_id: readClaims()?.userId, advanced_filter: JSON.stringify(v.filter) },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: savedKey() }),
  });
}

// `DeleteActiveSessionFilter`
export function useDeleteSavedFilter() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (f: SavedFilter) =>
      apiRequest<unknown>("/ht/delete-active-session-filter", { method: "POST", body: { id: f.id, user_id: f.user_id } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: savedKey() }),
  });
}
